// src/components/PublicHeader.jsx - FIXED WITH ADAPTIVE AUTH BUTTONS + LANGUAGE SWITCHER
import React, { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Search, Menu, X, MapPin, LayoutDashboard } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import LanguageSwitcher from './LanguageSwitcher';

import { useTranslation } from 'react-i18next';
import NavCurrencySwitcher from './NavCurrencySwitcher';

const SearchPopup = ({ isOpen, onClose }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [location, setLocation] = useState('');
    const navigate = useNavigate();
    const { t } = useTranslation();

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchTerm.trim()) {
            const query = new URLSearchParams();
            query.append('q', searchTerm.trim());
            if (location.trim()) query.append('loc', location.trim());
            navigate(`/gigs?${query.toString()}`);
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 bg-white/95 backdrop-blur-sm flex flex-col p-6 transition-opacity duration-300">
            <div className="flex justify-between items-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900">{t('quick_search')}</h2>
                <button
                    onClick={onClose}
                    className="p-2 text-gray-600 hover:text-blue-600 rounded-full hover:bg-gray-100"
                >
                    <X className="w-6 h-6" />
                </button>
            </div>
            <form onSubmit={handleSearch} className="flex flex-col space-y-4 max-w-2xl mx-auto w-full">
                <div className="relative">
                    <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <input
                        type="text"
                        placeholder={t('what_service_looking_for')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full p-3 pl-10 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                        autoFocus
                    />
                </div>
                <div className="relative">
                    <MapPin className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <input
                        type="text"
                        placeholder={t('location_placeholder')}
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        className="w-full p-3 pl-10 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>
                <button
                    type="submit"
                    className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition duration-150"
                >
                    {t('search_gigship')}
                </button>
            </form>
        </div>
    );
};

const PublicHeader = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const { isAuthenticated } = useAuth();
    const { t } = useTranslation();

    const navItems = [
        { name: t('nav.home'), to: '/' },
        { name: t('nav.howItWorks'), to: '/how-it-works' },
        { name: t('nav.gigs'), to: '/gigs' },
        { name: t('nav.requests'), to: '/requests' },
        { name: t('nav.giggers'), to: '/giggers' },
    ];

    return (
        <header className="sticky top-0 z-50 bg-white shadow-md">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16">
                    {/* Logo */}
                    <Link to="/" className="flex items-center py-1">
                        {/* Desktop logo (hidden on mobile) */}
                        <img 
                            src="/gigship-logo.png"
                            alt="GigShip Logo"
                            className="hidden md:block h-8 w-auto"
                        />
                        {/* Mobile/PWA logo (hidden on desktop) */}
                        <img 
                            src="/android-chrome-512x512.png"
                            alt="GigShip Logo"
                            className="block md:hidden h-10 w-10 object-contain rounded-lg"
                        />
                    </Link>

                    {/* Desktop Navigation */}
                    <nav className="hidden lg:flex space-x-6 items-center">
                        {navItems.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                className={({ isActive }) =>
                                    `text-sm font-medium transition-colors ${isActive
                                        ? 'text-blue-600 border-b-2 border-blue-600'
                                        : 'text-gray-600 hover:text-blue-600'
                                    }`
                                }
                            >
                                {item.name}
                            </NavLink>
                        ))}

                        {/* Desktop Search Button */}
                        <button
                            onClick={() => setIsSearchOpen(true)}
                            className="text-gray-600 hover:text-blue-600 p-2 rounded-full hover:bg-gray-50 transition-colors"
                            aria-label="Open search"
                        >
                            <Search className="w-5 h-5" />
                        </button>

                        {/* Language & Currency Switchers */}
                        <div className="flex items-center gap-2 ml-2">
                            <LanguageSwitcher variant="compact" />
                            <NavCurrencySwitcher />
                        </div>
                    </nav>

                    {/* Auth Buttons (Desktop) */}
                    <div className="hidden lg:flex items-center space-x-3">
                        {isAuthenticated ? (
                            <Link
                                to="/dashboard"
                                className="flex items-center gap-2 text-sm font-medium bg-blue-600 text-white px-5 py-2 rounded-full hover:bg-blue-700 transition-colors"
                            >
                                <LayoutDashboard className="w-4 h-4" />
                                {t('nav.dashboard')}
                            </Link>
                        ) : (
                            <>
                                <Link
                                    to="/login"
                                    className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors px-3 py-2"
                                >
                                    {t('nav.signIn')}
                                </Link>
                                <Link
                                    to="/register"
                                    className="text-sm font-medium bg-blue-600 text-white px-4 py-2 rounded-full hover:bg-blue-700 transition-colors"
                                >
                                    {t('nav.getStarted')}
                                </Link>
                            </>
                        )}
                    </div>

                    {/* Mobile Menu & Search */}
                    <div className="lg:hidden flex items-center space-x-2">
                        {/* Mobile Search Button */}
                        <button
                            onClick={() => setIsSearchOpen(true)}
                            className="p-2 text-gray-600 hover:text-blue-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <Search className="w-6 h-6" />
                        </button>

                        {/* Mobile Language & Currency Switchers */}
                        <div className="flex items-center gap-1 ml-1">
                            <LanguageSwitcher variant="compact" />
                            <NavCurrencySwitcher />
                        </div>

                        {/* Mobile Menu Button */}
                        <button
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            className="p-2 text-gray-600 hover:text-blue-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <Menu className="w-6 h-6" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Dropdown Menu */}
            {isMenuOpen && (
                <div className="lg:hidden absolute top-16 left-0 right-0 bg-white shadow-xl border-t border-gray-100 pb-4 z-40">
                    <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
                        {navItems.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                onClick={() => setIsMenuOpen(false)}
                                className={({ isActive }) =>
                                    `block px-3 py-2 rounded-md text-base font-medium transition-colors ${isActive
                                        ? 'bg-blue-50 text-blue-600'
                                        : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600'
                                    }`
                                }
                            >
                                {item.name}
                            </NavLink>
                        ))}
                    </div>

                    {/* Mobile Auth Buttons */}
                    <div className="pt-4 pb-2 border-t border-gray-100 space-y-2 px-5">
                        {isAuthenticated ? (
                            <Link
                                to="/dashboard"
                                className="flex items-center justify-center gap-2 w-full px-5 py-2 bg-blue-600 text-white rounded-full text-base font-medium hover:bg-blue-700"
                                onClick={() => setIsMenuOpen(false)}
                            >
                                <LayoutDashboard className="w-5 h-5" />
                                {t('go_to_dashboard')}
                            </Link>
                        ) : (
                            <>
                                <Link
                                    to="/login"
                                    className="block w-full text-center px-5 py-2 border border-gray-300 rounded-full text-base font-medium text-gray-700 hover:bg-gray-50"
                                    onClick={() => setIsMenuOpen(false)}
                                >
                                    {t('nav.signIn')}
                                </Link>
                                <Link
                                    to="/register"
                                    className="block w-full text-center px-5 py-2 bg-blue-600 text-white rounded-full text-base font-medium hover:bg-blue-700"
                                    onClick={() => setIsMenuOpen(false)}
                                >
                                    {t('nav.getStarted')}
                                </Link>
                            </>
                        )}

                        {/* GigShip Go Mobile Button */}
                        <Link
                            to="/go"
                            className="block w-full text-center px-5 py-2 mt-4 bg-gradient-to-r from-orange-500 to-amber-900 text-white rounded-full text-base font-bold shadow-md hover:from-orange-600 hover:to-amber-950 transition-all duration-300"
                            onClick={() => setIsMenuOpen(false)}
                        >
                            {t('nav.go')}
                        </Link>
                    </div>
                </div>
            )}

            {/* Search Popup */}
            <SearchPopup isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
        </header>
    );
};

export default PublicHeader;